import api from './client'
import { toNum } from './global'

/**
 * `/api/cluster/status` (public summary) — served by
 * `internal/api/webui.go: handleWebUIClusterStatusPublic`, which returns the map
 * built by `internal/cluster/cluster.go: getClusterStatus(false)`.
 *
 * Note: this endpoint replies with the **bare** object (no `{ok,data}` envelope),
 * and returns `{cluster_enabled: false, version}` when clustering is disabled.
 */
export interface RawClusterStatusPublic {
  cluster_enabled: boolean
  node_id?: string
  total_nodes?: number
  online_nodes?: number
  offline_nodes?: number
  tls?: boolean
  tls_mode?: string
  task_count?: number
  version?: string
  data_dir?: string
  disk_total?: number
  disk_free?: number
  disk_used?: number
  /** v3.13.67: config 启用了集群但启动失败的降级标记 */
  cluster_degraded?: boolean
  degraded_reason?: string
}

export interface ClusterSummary {
  enabled: boolean
  nodeId: string
  totalNodes: number
  onlineNodes: number
  offlineNodes: number
  taskCount: number
  version: string
  /** bytes; 0 when the backend could not stat the data dir */
  diskTotal: number
  diskFree: number
  diskUsed: number
  /** true when disk figures are present and usable */
  hasDisk: boolean
  /** v3.13.67: 集群静默降级 (启用但启动失败) */
  degraded: boolean
  degradedReason: string
}

export async function getClusterSummary(): Promise<ClusterSummary> {
  const raw = await api.get<RawClusterStatusPublic>('/api/cluster/status', { silent: true })
  const diskTotal = toNum(raw?.disk_total)
  const diskUsed = toNum(raw?.disk_used)
  return {
    enabled: Boolean(raw?.cluster_enabled),
    nodeId: raw?.node_id ?? '',
    totalNodes: toNum(raw?.total_nodes),
    onlineNodes: toNum(raw?.online_nodes),
    offlineNodes: toNum(raw?.offline_nodes),
    taskCount: toNum(raw?.task_count),
    version: raw?.version ?? '',
    diskTotal,
    diskFree: toNum(raw?.disk_free),
    diskUsed,
    hasDisk: diskTotal > 0,
    degraded: Boolean(raw?.cluster_degraded),
    degradedReason: raw?.degraded_reason ?? '',
  }
}
